import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Baby, Droplet, HeartPulse, Activity, ShieldAlert, Shield, AlertTriangle,
  Zap, Wind, Plus, X, Pencil, Info,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const ICONS = { Baby, Droplet, HeartPulse, Activity, ShieldAlert, Shield, AlertTriangle, Zap, Wind };
const TONE_CLASSES = {
  pink:   { badge: "bg-[#FDE7EF] text-[#8A2D5C] border-[#F5B4CC]", alert: "border-[#F5B4CC] bg-[#FDE7EF]/40" },
  amber:  { badge: "bg-[#FDF3ED] text-[#B0521F] border-[#E5B34B]", alert: "border-[#E5B34B] bg-[#FFF8E7]" },
  orange: { badge: "bg-[#FDE9DC] text-[#B0521F] border-[#F0B48C]", alert: "border-[#F0B48C] bg-[#FDE9DC]/40" },
  red:    { badge: "bg-[#FFE9E9] text-[#8B1E1E] border-[#F0A5A5]", alert: "border-[#F0A5A5] bg-[#FFF3F3]" },
  purple: { badge: "bg-[#EDE5F0] text-[#4A2D5C] border-[#C9A5D8]", alert: "border-[#C9A5D8] bg-[#EDE5F0]/40" },
  violet: { badge: "bg-[#E5E2F0] text-[#4A3D8A] border-[#B5AED8]", alert: "border-[#B5AED8] bg-[#E5E2F0]/40" },
  yellow: { badge: "bg-[#FFF6D9] text-[#8A6C1F] border-[#E5D07A]", alert: "border-[#E5D07A] bg-[#FFF6D9]/40" },
  sky:    { badge: "bg-[#DDECF5] text-[#1F6B8A] border-[#9EC4DC]", alert: "border-[#9EC4DC] bg-[#DDECF5]/40" },
};

export default function SpecialConditions({ patient, onReload }) {
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Set(patient?.special_conditions || []));

  useEffect(() => {
    api.get("/special-conditions").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    setSelected(new Set(patient?.special_conditions || []));
  }, [patient?.special_conditions]);

  const catalogMap = useMemo(() => Object.fromEntries(catalog.map((c) => [c.key, c])), [catalog]);
  const active = (patient?.special_conditions || []).map((k) => catalogMap[k]).filter(Boolean);

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const save = async () => {
    try {
      await api.patch(`/patients/${patient.id}/special-conditions`, { tags: Array.from(selected) });
      toast.success("Conditions updated");
      setOpen(false);
      onReload?.();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };

  // Merge all alerts across all active conditions (dedup by string)
  const allAlerts = useMemo(() => {
    const map = new Map(); // alert -> [condition_label]
    for (const c of active) {
      for (const a of (c.alerts || [])) {
        const key = a;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(c.label);
      }
    }
    return Array.from(map.entries()).map(([alert, sources]) => ({ alert, sources }));
  }, [active]);

  return (
    <>
      {/* Compact badges row + edit trigger */}
      <div className="flex items-center gap-2 flex-wrap mt-2" data-testid="conditions-badges">
        {active.map((c) => {
          const Icon = ICONS[c.icon] || AlertTriangle;
          const tone = TONE_CLASSES[c.tone] || TONE_CLASSES.amber;
          return (
            <span key={c.key} data-testid={`condition-badge-${c.key}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${tone.badge}`}>
              <Icon className="w-3 h-3" />
              {c.label}
            </span>
          );
        })}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="conditions-edit-btn"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-pine/20 text-[11px] text-ink-mid hover:bg-linen">
              {active.length === 0 ? <><Plus className="w-3 h-3" /> Add condition</> : <><Pencil className="w-3 h-3" /> Edit</>}
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl">Clinical safety conditions</DialogTitle></DialogHeader>
            <p className="text-xs text-ink-mid -mt-2 mb-3">Select all that apply. Alerts appear on the patient profile every visit.</p>
            <div className="max-h-96 overflow-y-auto space-y-1.5 pr-1" data-testid="conditions-catalog">
              {catalog.map((c) => {
                const Icon = ICONS[c.icon] || AlertTriangle;
                const tone = TONE_CLASSES[c.tone] || TONE_CLASSES.amber;
                const isOn = selected.has(c.key);
                return (
                  <label key={c.key} data-testid={`condition-opt-${c.key}`}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      isOn ? tone.badge + " border" : "border-[#E8EAE9] hover:bg-linen/50"
                    }`}>
                    <input type="checkbox" checked={isOn} onChange={() => toggle(c.key)} className="mt-0.5 w-4 h-4 accent-pine" />
                    <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{c.label}</div>
                      <div className={`text-[11px] mt-0.5 ${isOn ? "" : "text-ink-mid"}`}>
                        {c.alerts[0]}{c.alerts.length > 1 ? ` +${c.alerts.length - 1} more` : ""}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <button data-testid="conditions-save" onClick={save} className="w-full btn-primary text-sm mt-3">Save conditions</button>
          </DialogContent>
        </Dialog>
      </div>

      {/* Clinical safety alerts banner (only if any active conditions) */}
      {allAlerts.length > 0 && (
        <div className="mt-4 rounded-2xl border-2 border-[#E5B34B] bg-[#FFF8E7] p-4" data-testid="conditions-alert-banner">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-[#E5B34B]/25 grid place-items-center flex-shrink-0">
              <Info className="w-5 h-5 text-[#B0521F]" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-[#B0521F]">Clinical safety — dental precautions</div>
              <div className="text-xs text-ink-mid mt-0.5">This patient has {active.length} flagged condition{active.length > 1 ? "s" : ""}. Review before every procedure:</div>
            </div>
          </div>
          <ul className="space-y-1.5 pl-1">
            {allAlerts.map(({ alert, sources }, i) => (
              <li key={i} data-testid={`condition-alert-${i}`} className="flex items-start gap-2 text-sm text-ink">
                <span className="text-[#B0521F] font-bold mt-0.5">•</span>
                <div className="min-w-0">
                  <span>{alert}</span>
                  <span className="text-[10px] text-ink-soft ml-2">({sources.join(", ")})</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
